import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { canUserDelete, canUserEdit } from '@/utils/permissions';
import {
  ShoppingCart, Plus, Search, Edit2, Trash2, Save, X, RefreshCw,
  FileText, Download, Eye, ChevronDown, ChevronUp, Package, Building2,
  Calendar, DollarSign, Truck, CheckCircle, AlertCircle, Globe, Loader2,
  History, Mail
} from 'lucide-react';
import EditHistoryModal from '../../components/EditHistoryModal';
import EmailModal from '../../components/EmailModal';
import EmailStatusBadge, { isUnemailed } from '../../components/EmailStatusBadge';

const STATUS_OPTIONS = ['Draft', 'Sent', 'Confirmed', 'Partially Received', 'Completed', 'Cancelled'];
const DUTY_OPTIONS = ['FTA', 'Non-FTA'];

// Scrap Types for Raw Material PO - with common HSN code
const SCRAP_TYPE_OPTIONS = [
  'TALDON',
  'TAINT TABOR',
  'TENSE',
  'TELIC',
  'TALC',
  'TERSE',
  'TALLY',
  'TWITCH',
  'ZORBA',
  'TABLET',
  'TRUMP',
  'TEENS',
  'TASTE',
  'TEPID',
  'TREAD',
];
const DEFAULT_HSN_CODE = '76020010'; // Common HSN for aluminium scrap

const CURRENCY_OPTIONS = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'CHF', symbol: 'Fr', name: 'Swiss Franc' },
  { code: 'SAR', symbol: '﷼', name: 'Saudi Riyal' },
  { code: 'KWD', symbol: 'د.ك', name: 'Kuwaiti Dinar' },
  { code: 'OMR', symbol: 'ر.ع.', name: 'Omani Rial' },
  { code: 'QAR', symbol: 'ر.ق', name: 'Qatari Riyal' },
];

// Custom Async Searchable Dropdown for Suppliers
function SupplierDropdown({ value, displayValue, onSelect, placeholder = "Search supplier...", disabled = false }) {
  const { API } = useContext(AppContext);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  const handleSearch = useCallback(async (term) => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    
    searchTimeoutRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/master-data/raw-material/suppliers`);
        const suppliers = response.data.data || [];
        const filtered = suppliers.filter(s => 
          s.name?.toLowerCase().includes(term.toLowerCase()) ||
          s.code?.toLowerCase().includes(term.toLowerCase())
        ).slice(0, 20);
        setOptions(filtered);
      } catch (error) {
        console.error('Error loading suppliers:', error);
        setOptions([]);
      }
      setLoading(false);
    }, 300);
  }, [API]);

  useEffect(() => {
    if (isOpen) handleSearch(searchTerm);
  }, [isOpen, searchTerm, handleSearch]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full px-3 py-2 border rounded-lg text-sm text-left flex items-center justify-between ${
          disabled ? 'bg-gray-100 border-gray-200' : 'border-gray-300 hover:border-green-400'
        }`}
        data-testid="supplier-dropdown"
      >
        <span className={displayValue ? 'text-gray-900' : 'text-gray-400'}>
          {displayValue || placeholder}
        </span>
        <ChevronDown size={16} className={`text-gray-400 transition ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-72 overflow-hidden">
          <div className="p-2 border-b border-gray-100 sticky top-0 bg-white">
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
              <Search size={16} className="text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Type to search..."
                className="flex-1 bg-transparent border-none outline-none text-sm"
                autoFocus
              />
              {loading && <Loader2 size={16} className="animate-spin text-gray-400" />}
            </div>
          </div>
          <div className="max-h-56 overflow-y-auto">
            {options.length === 0 ? (
              <div className="px-3 py-4 text-center text-gray-500 text-sm">
                {loading ? 'Loading...' : 'No suppliers found'}
              </div>
            ) : (
              options.map((supplier) => (
                <button
                  key={supplier.id}
                  type="button"
                  onClick={() => { onSelect(supplier); setIsOpen(false); setSearchTerm(''); }}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-green-50 transition flex items-center gap-2"
                >
                  <Building2 size={14} className="text-gray-400" />
                  <div>
                    <div className="font-medium text-gray-900">{supplier.name}</div>
                    <div className="text-xs text-gray-500">{supplier.code} | {supplier.country || 'India'}</div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function RawMaterialPurchaseOrder() {
  const { API, user } = useContext(AppContext);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPO, setEditingPO] = useState(null);
  const [expandedPO, setExpandedPO] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [message, setMessage] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [historyPO, setHistoryPO] = useState(null);
  const [emailPO, setEmailPO] = useState(null);
  const [showOnlyUnemailed, setShowOnlyUnemailed] = useState(false);

  /**
   * Open the Email PO modal with the LATEST supplier email from master data.
   * The PO record's `supplierEmail` is a snapshot taken at creation time;
   * if the user updated the supplier email in Master Data later, the snapshot
   * is stale. We always re-fetch by supplier code / name here.
   */
  const openEmailModal = async (po) => {
    if (!po) return;
    try {
      const r = await axios.get(`${API}/master-data/raw-material/suppliers`);
      const suppliers = r?.data?.data || [];
      const match = suppliers.find(s =>
        (po.supplierId && s.id === po.supplierId) ||
        (po.supplierCode && s.code === po.supplierCode) ||
        (po.supplierName && (s.name || '').trim().toLowerCase() === (po.supplierName || '').trim().toLowerCase())
      );
      const freshEmail = match?.email || '';
      setEmailPO({
        ...po,
        supplierEmail: freshEmail || po.supplierEmail || '',
        snapshotEmail: po.supplierEmail || '',
      });
    } catch (e) {
      setEmailPO({ ...po, snapshotEmail: po.supplierEmail || '' });
    }
  };
  
  // Check if user can delete (Admin or 'full' access)
  const canDelete = canUserDelete(user, '/raw-material/purchase-order');
  const canEdit = canUserEdit(user, '/raw-material/purchase-order');

  // PO Value Filter State
  const [valueFilterType, setValueFilterType] = useState('all'); // all, month, year, custom
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);

  // Form state
  const [formData, setFormData] = useState({
    supplierName: '',
    supplierCode: '',
    supplierGst: '',
    supplierContact: '',
    supplierContactName: '',
    supplierEmail: '',
    supplierAddress: '',
    supplierCity: '',
    supplierState: '',
    supplierCountry: '',
    poDate: new Date().toISOString().split('T')[0],
    deliveryDate: '',
    deliveryEndDate: '',
    paymentTerms: '',
    termsConditions: '',
    duty: 'Non-FTA',
    currency: 'USD',
    remarks: '',
    items: [{ scrapType: '', hsnCode: '', quantity: '', rate: '', amount: 0, gstRate: 18 }],
  });

  // Master data for dropdowns
  const [paymentTermsList, setPaymentTermsList] = useState([]);
  const [termsConditionsList, setTermsConditionsList] = useState([]);
  const [scrapTypes, setScrapTypes] = useState([]);

  useEffect(() => {
    fetchPurchaseOrders();
    fetchMasterData();
  }, []);

  const fetchPurchaseOrders = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/raw-material/purchase-orders`);
      setPurchaseOrders(response.data.data || []);
    } catch (error) {
      console.error('Error fetching POs:', error);
    }
    setLoading(false);
  };

  const fetchMasterData = async () => {
    try {
      // Fetch payment terms
      const paymentRes = await axios.get(`${API}/master-data/stores/payment_terms`);
      setPaymentTermsList(paymentRes.data.data || []);

      // Fetch terms & conditions
      const termsRes = await axios.get(`${API}/master-data/stores/terms_conditions`);
      setTermsConditionsList(termsRes.data.data || []);

      // Fetch scrap types
      const scrapRes = await axios.get(`${API}/master-data/raw-material/scrap_types`);
      setScrapTypes(scrapRes.data.data || []);
    } catch (error) {
      console.error('Error fetching master data:', error);
    }
  };

  const handleSupplierSelect = (supplier) => {
    if (supplier) {
      setFormData(prev => ({
        ...prev,
        supplierName: supplier.name || '',
        supplierCode: supplier.code || '',
        supplierGst: supplier.gst || '',
        supplierContact: supplier.contact || '',
        supplierContactName: supplier.contactName || '',
        supplierEmail: supplier.email || '',
        supplierAddress: supplier.address || '',
        supplierCity: supplier.city || '',
        supplierState: supplier.state || '',
        supplierCountry: supplier.country || '',
      }));
    }
  };

  const addLineItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { scrapType: '', hsnCode: '', quantity: '', rate: '', amount: 0, gstRate: 18 }]
    }));
  };

  const removeLineItem = (index) => {
    if (formData.items.length > 1) {
      setFormData(prev => ({
        ...prev,
        items: prev.items.filter((_, i) => i !== index)
      }));
    }
  };

  const updateLineItem = (index, field, value) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], [field]: value };
      
      // Auto-fill HSN code when scrap type is selected
      if (field === 'scrapType' && value) {
        newItems[index].hsnCode = DEFAULT_HSN_CODE;
      }
      
      // Calculate amount
      if (field === 'quantity' || field === 'rate') {
        const qty = parseFloat(newItems[index].quantity) || 0;
        const rate = parseFloat(newItems[index].rate) || 0;
        newItems[index].amount = qty * rate;
      }
      
      return { ...prev, items: newItems };
    });
  };

  const calculateSubtotal = () => {
    return formData.items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
  };

  const isINR = () => (formData.currency || '').toUpperCase() === 'INR';

  const calculateGstTotal = () => {
    if (!isINR()) return 0;
    return formData.items.reduce((sum, item) => {
      const amt = parseFloat(item.amount) || 0;
      const gst = parseFloat(item.gstRate);
      const rate = Number.isFinite(gst) ? gst : 18;
      return sum + (amt * rate / 100);
    }, 0);
  };

  const calculateTotal = () => calculateSubtotal() + calculateGstTotal();

  // Get currency symbol
  const getCurrencySymbol = (code) => {
    const currency = CURRENCY_OPTIONS.find(c => c.code === code);
    return currency ? currency.symbol : '$';
  };

  const resetForm = () => {
    setFormData({
      supplierName: '',
      supplierCode: '',
      supplierGst: '',
      supplierContact: '',
      supplierContactName: '',
      supplierEmail: '',
      supplierAddress: '',
      supplierCity: '',
      supplierState: '',
      supplierCountry: '',
      poDate: new Date().toISOString().split('T')[0],
      deliveryDate: '',
    deliveryEndDate: '',
      paymentTerms: '',
      termsConditions: '',
      duty: 'Non-FTA',
      currency: 'USD',
      remarks: '',
      items: [{ scrapType: '', hsnCode: '', quantity: '', rate: '', amount: 0, gstRate: 18 }],
    });
    setEditingPO(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.supplierName) {
      setMessage({ type: 'error', text: 'Please select a supplier' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    // Validate no backdate for new POs
    if (!editingPO) {
      const today = new Date().toISOString().split('T')[0];
      if (formData.poDate < today) {
        setMessage({ type: 'error', text: 'PO Date cannot be in the past. Backdated POs are not allowed.' });
        setTimeout(() => setMessage(null), 4000);
        return;
      }
    }

    if (formData.items.every(item => !item.scrapType)) {
      setMessage({ type: 'error', text: 'Please add at least one line item' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    try {
      const payload = {
        ...formData,
        totalAmount: calculateTotal(),
        status: editingPO ? formData.status : 'Draft',
      };

      if (editingPO) {
        await axios.put(`${API}/raw-material/purchase-orders/${editingPO}`, payload);
        setMessage({ type: 'success', text: 'Purchase Order updated successfully!' });
      } else {
        await axios.post(`${API}/raw-material/purchase-orders`, payload);
        setMessage({ type: 'success', text: 'Purchase Order created successfully!' });
      }
      
      fetchPurchaseOrders();
      resetForm();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to save Purchase Order' });
    }
    setTimeout(() => setMessage(null), 3000);
  };

  const handleEdit = (po) => {
    setFormData({
      supplierName: po.supplierName || '',
      supplierCode: po.supplierCode || '',
      supplierGst: po.supplierGst || '',
      supplierContact: po.supplierContact || '',
      supplierContactName: po.supplierContactName || '',
      supplierEmail: po.supplierEmail || '',
      supplierAddress: po.supplierAddress || '',
      supplierCity: po.supplierCity || '',
      supplierState: po.supplierState || '',
      supplierCountry: po.supplierCountry || '',
      poDate: po.poDate || '',
      deliveryDate: po.deliveryDate || '',
      deliveryEndDate: po.deliveryEndDate || '',
      paymentTerms: po.paymentTerms || '',
      termsConditions: po.termsConditions || '',
      duty: po.duty || 'Non-FTA',
      currency: po.currency || 'USD',
      remarks: po.remarks || '',
      items: po.items?.length > 0
        ? po.items.map(it => ({ ...it, gstRate: it.gstRate ?? (po.currency === 'INR' ? 18 : 0) }))
        : [{ scrapType: '', hsnCode: '', quantity: '', rate: '', amount: 0, gstRate: po.currency === 'INR' ? 18 : 0 }],
      status: po.status || 'Draft',
    });
    setEditingPO(po.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this Purchase Order?')) return;
    try {
      await axios.delete(`${API}/raw-material/purchase-orders/${id}`);
      setMessage({ type: 'success', text: 'Purchase Order deleted!' });
      fetchPurchaseOrders();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete Purchase Order' });
    }
    setTimeout(() => setMessage(null), 3000);
  };

  const handleDownloadPDF = async (po) => {
    setDownloading(po.id);
    try {
      const response = await axios.get(`${API}/raw-material/purchase-orders/${po.id}/pdf?t=${Date.now()}`, {
        responseType: 'blob',
        headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `PO_${po.poNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to download PDF' });
      setTimeout(() => setMessage(null), 3000);
    }
    setDownloading(null);
  };

  // Filter POs
  const filteredPOs = purchaseOrders.filter(po => {
    const matchesSearch = !searchTerm || 
      po.poNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      po.supplierName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      po.salesContractNumber?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || po.status === statusFilter;
    const matchesUnemailed = !showOnlyUnemailed || isUnemailed(po);
    return matchesSearch && matchesStatus && matchesUnemailed;
  });

  const unemailedCount = purchaseOrders.filter(isUnemailed).length;

  const getStatusColor = (status) => {
    const colors = {
      'Draft': 'bg-gray-100 text-gray-700',
      'Sent': 'bg-blue-100 text-blue-700',
      'Confirmed': 'bg-green-100 text-green-700',
      'Partially Received': 'bg-yellow-100 text-yellow-700',
      'Completed': 'bg-emerald-100 text-emerald-700',
      'Cancelled': 'bg-red-100 text-red-700',
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  // Calculate filtered PO value
  const getFilteredPOValue = () => {
    let filtered = purchaseOrders;
    
    if (valueFilterType !== 'all') {
      filtered = purchaseOrders.filter(po => {
        const poDate = new Date(po.poDate || po.createdAt);
        const poYear = poDate.getFullYear();
        const poMonth = poDate.getMonth() + 1;
        
        if (valueFilterType === 'year') {
          return poYear === selectedYear;
        } else if (valueFilterType === 'month') {
          return poMonth === selectedMonth && poYear === new Date().getFullYear();
        } else if (valueFilterType === 'custom') {
          return poYear === selectedYear && poMonth === selectedMonth;
        }
        return true;
      });
    }
    
    // Calculate total value by currency
    const totals = {};
    filtered.forEach(po => {
      const currency = po.currency || 'USD';
      const total = po.items?.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0) || parseFloat(po.totalAmount) || 0;
      totals[currency] = (totals[currency] || 0) + total;
    });
    
    return { count: filtered.length, totals };
  };

  const poStats = getFilteredPOValue();
  const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i);
  const months = [
    { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
    { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
    { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
    { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' }
  ];

  return (
    <div className="space-y-6" data-testid="import-po-page">
      {/* PO Value Stats Card */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-xl p-5 text-white shadow-lg">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <DollarSign size={20} />
              <span className="text-blue-100 text-sm font-medium">Total Purchase Order Value</span>
            </div>
            <div className="flex flex-wrap items-baseline gap-4">
              {Object.keys(poStats.totals).length > 0 ? (
                Object.entries(poStats.totals).map(([currency, value]) => {
                  const curr = CURRENCY_OPTIONS.find(c => c.code === currency) || { symbol: currency };
                  return (
                    <div key={currency} className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold">{curr.symbol}{value.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                      <span className="text-blue-200 text-sm">{currency}</span>
                    </div>
                  );
                })
              ) : (
                <span className="text-3xl font-bold">$0</span>
              )}
            </div>
            <p className="text-blue-200 text-sm mt-1">{poStats.count} Purchase Order(s)</p>
          </div>
          
          {/* Filter Controls */}
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={valueFilterType}
              onChange={(e) => setValueFilterType(e.target.value)}
              className="px-3 py-2 bg-white/20 border border-white/30 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/50"
            >
              <option value="all" className="text-gray-800">All Time</option>
              <option value="year" className="text-gray-800">This Year</option>
              <option value="month" className="text-gray-800">This Month</option>
              <option value="custom" className="text-gray-800">Custom</option>
            </select>
            
            {(valueFilterType === 'year' || valueFilterType === 'custom') && (
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="px-3 py-2 bg-white/20 border border-white/30 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/50"
              >
                {years.map(year => (
                  <option key={year} value={year} className="text-gray-800">{year}</option>
                ))}
              </select>
            )}
            
            {(valueFilterType === 'month' || valueFilterType === 'custom') && (
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="px-3 py-2 bg-white/20 border border-white/30 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/50"
              >
                {months.map(month => (
                  <option key={month.value} value={month.value} className="text-gray-800">{month.label}</option>
                ))}
              </select>
            )}
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg">
            <ShoppingCart className="text-white" size={24} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Raw Material Purchase Orders</h1>
            <p className="text-sm text-gray-500">Create and manage purchase orders for raw materials</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchPurchaseOrders}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
          <button
            onClick={() => { resetForm(); setShowForm(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:opacity-90 transition shadow-md"
            data-testid="create-po-btn"
          >
            <Plus size={18} />
            Create PO
          </button>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg flex items-center gap-2 ${
          message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
        }`}>
          {message.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
          {message.text}
        </div>
      )}

      {/* Create/Edit Form */}
      {showForm && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-4 bg-gradient-to-r from-green-500 to-green-600">
            <h2 className="text-lg font-bold text-white">
              {editingPO ? 'Edit Purchase Order' : 'Create New Purchase Order'}
            </h2>
          </div>
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Supplier Selection */}
            <div className="bg-green-50 rounded-xl p-4 border border-green-200">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Building2 size={18} className="text-green-600" />
                Supplier Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Supplier Name <span className="text-red-500">*</span>
                  </label>
                  <SupplierDropdown
                    value={formData.supplierCode}
                    displayValue={formData.supplierName ? `${formData.supplierName} (${formData.supplierCode || 'N/A'})` : ''}
                    onSelect={handleSupplierSelect}
                    placeholder="Search supplier..."
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Supplier Code</label>
                  <input
                    type="text"
                    value={formData.supplierCode}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">GST No</label>
                  <input
                    type="text"
                    value={formData.supplierGst}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Contact Name</label>
                  <input
                    type="text"
                    value={formData.supplierContactName}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Contact Number</label>
                  <input
                    type="text"
                    value={formData.supplierContact}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Supplier Email</label>
                  <input
                    type="email"
                    value={formData.supplierEmail}
                    onChange={(e) => setFormData({ ...formData, supplierEmail: e.target.value })}
                    placeholder="supplier@example.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-green-400 outline-none"
                    title="Pre-fills the Email-PO modal. Edit/add if missing."
                    data-testid="import-po-supplier-email-input"
                  />
                </div>
                <div className="md:col-span-3">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Address</label>
                  <input
                    type="text"
                    value={[formData.supplierAddress, formData.supplierCity, formData.supplierState, formData.supplierCountry].filter(Boolean).join(', ')}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm"
                  />
                </div>
              </div>
            </div>

            {/* PO Details */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  PO Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.poDate}
                  min={editingPO ? undefined : new Date().toISOString().split('T')[0]}
                  onChange={(e) => setFormData(prev => ({ ...prev, poDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-green-400 focus:ring-2 focus:ring-green-100 outline-none text-sm"
                  required
                />
                {!editingPO && <p className="text-xs text-gray-400 mt-1">Backdate not allowed</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Delivery Start Date</label>
                <input
                  type="date"
                  value={formData.deliveryDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, deliveryDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-green-400 focus:ring-2 focus:ring-green-100 outline-none text-sm"
                  data-testid="rm-po-delivery-date"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Delivery End Date</label>
                <input
                  type="date"
                  value={formData.deliveryEndDate}
                  min={formData.deliveryDate || undefined}
                  onChange={(e) => setFormData(prev => ({ ...prev, deliveryEndDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-green-400 focus:ring-2 focus:ring-green-100 outline-none text-sm"
                  data-testid="rm-po-delivery-end-date"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Currency <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.currency}
                  onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-green-400 focus:ring-2 focus:ring-green-100 outline-none text-sm"
                  required
                >
                  {CURRENCY_OPTIONS.map(curr => (
                    <option key={curr.code} value={curr.code}>{curr.code} - {curr.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Payment Terms</label>
                <select
                  value={formData.paymentTerms}
                  onChange={(e) => setFormData(prev => ({ ...prev, paymentTerms: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-green-400 focus:ring-2 focus:ring-green-100 outline-none text-sm"
                >
                  <option value="">Select...</option>
                  {paymentTermsList.map(pt => (
                    <option key={pt.id} value={pt.paymentName}>{pt.paymentName}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Duty Type</label>
                <select
                  value={formData.duty}
                  onChange={(e) => setFormData(prev => ({ ...prev, duty: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-green-400 focus:ring-2 focus:ring-green-100 outline-none text-sm"
                >
                  {DUTY_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Line Items */}
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                  <Package size={18} className="text-green-600" />
                  Line Items
                </h3>
                <button
                  type="button"
                  onClick={addLineItem}
                  className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition"
                >
                  <Plus size={14} />
                  Add Item
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">#</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Scrap Type</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">HSN Code</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Quantity (MT)</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Rate ({formData.currency}/MT)</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Amount ({formData.currency})</th>
                      {isINR() && (
                        <>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">GST %</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">GST Amt</th>
                        </>
                      )}
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {formData.items.map((item, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-500">{idx + 1}</td>
                        <td className="px-4 py-3">
                          <select
                            value={item.scrapType}
                            onChange={(e) => updateLineItem(idx, 'scrapType', e.target.value)}
                            className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:border-green-400 outline-none"
                            data-testid={`scrap-type-select-${idx}`}
                          >
                            <option value="">Select scrap type...</option>
                            {SCRAP_TYPE_OPTIONS.map(type => (
                              <option key={type} value={type}>{type}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="text"
                            value={item.hsnCode}
                            onChange={(e) => updateLineItem(idx, 'hsnCode', e.target.value)}
                            placeholder="HSN Code"
                            className="w-28 px-2 py-1.5 border border-gray-300 rounded text-sm focus:border-green-400 outline-none bg-gray-50"
                            readOnly
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            step="0.001"
                            value={item.quantity}
                            onChange={(e) => updateLineItem(idx, 'quantity', e.target.value)}
                            placeholder="0.000"
                            className="w-24 px-2 py-1.5 border border-gray-300 rounded text-sm focus:border-green-400 outline-none"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            step="0.01"
                            value={item.rate}
                            onChange={(e) => updateLineItem(idx, 'rate', e.target.value)}
                            placeholder="0.00"
                            className="w-28 px-2 py-1.5 border border-gray-300 rounded text-sm focus:border-green-400 outline-none"
                          />
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {getCurrencySymbol(formData.currency)}{(item.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </td>
                        {isINR() && (
                          <>
                            <td className="px-4 py-3">
                              <input
                                type="number"
                                step="0.01"
                                value={item.gstRate ?? 18}
                                onChange={(e) => updateLineItem(idx, 'gstRate', e.target.value)}
                                className="w-20 px-2 py-1.5 border border-gray-300 rounded text-sm focus:border-green-400 outline-none"
                                data-testid={`gst-rate-input-${idx}`}
                              />
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                              {getCurrencySymbol(formData.currency)}
                              {(((parseFloat(item.amount) || 0) * (parseFloat(item.gstRate ?? 18) || 0) / 100)).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                            </td>
                          </>
                        )}
                        <td className="px-4 py-3 text-center">
                          {formData.items.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeLineItem(idx)}
                              className="p-1.5 bg-red-100 text-red-600 rounded hover:bg-red-200 transition"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-green-50 border-t-2 border-green-200">
                    {isINR() ? (
                      <>
                        <tr>
                          <td colSpan={5} className="px-4 py-2 text-right font-medium text-gray-700">
                            Subtotal ({formData.currency}):
                          </td>
                          <td colSpan={4} className="px-4 py-2 text-sm font-semibold text-gray-700">
                            {getCurrencySymbol(formData.currency)}{calculateSubtotal().toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={5} className="px-4 py-2 text-right font-medium text-gray-700">
                            GST (IGST) ({formData.currency}):
                          </td>
                          <td colSpan={4} className="px-4 py-2 text-sm font-semibold text-gray-700">
                            {getCurrencySymbol(formData.currency)}{calculateGstTotal().toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </td>
                        </tr>
                        <tr className="border-t-2 border-green-300">
                          <td colSpan={5} className="px-4 py-3 text-right font-bold text-gray-800">
                            Grand Total ({formData.currency}):
                          </td>
                          <td colSpan={4} className="px-4 py-3 text-lg font-bold text-green-700" data-testid="po-grand-total-inr">
                            {getCurrencySymbol(formData.currency)}{calculateTotal().toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </td>
                        </tr>
                      </>
                    ) : (
                      <tr>
                        <td colSpan={5} className="px-4 py-3 text-right font-bold text-gray-800">
                          Total Amount ({formData.currency}):
                        </td>
                        <td colSpan={2} className="px-4 py-3 text-lg font-bold text-green-700">
                          {getCurrencySymbol(formData.currency)}{calculateTotal().toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </td>
                      </tr>
                    )}
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Terms & Remarks */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Terms & Conditions <span className="text-gray-400 font-normal">(select multiple)</span>
                </label>
                <div className="border border-gray-300 rounded-lg max-h-40 overflow-y-auto bg-white p-2 space-y-1" data-testid="tc-multiselect">
                  {termsConditionsList.length === 0 && (
                    <p className="text-xs text-gray-400 px-1">No terms available. Add in Master Data → Terms.</p>
                  )}
                  {termsConditionsList.map(tc => {
                    const selectedArr = Array.isArray(formData.termsConditions)
                      ? formData.termsConditions
                      : (formData.termsConditions || '').split('\n').filter(Boolean);
                    const checked = selectedArr.includes(tc.termDescription);
                    return (
                      <label key={tc.id} className="flex items-start gap-2 px-2 py-1 text-xs rounded hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...selectedArr, tc.termDescription]
                              : selectedArr.filter(x => x !== tc.termDescription);
                            setFormData(prev => ({ ...prev, termsConditions: next.join('\n') }));
                          }}
                          className="mt-0.5 accent-green-600"
                        />
                        <span className="text-gray-700">{tc.termDescription}</span>
                      </label>
                    );
                  })}
                </div>
                {formData.termsConditions && (
                  <p className="text-[11px] text-gray-500 mt-1">
                    {formData.termsConditions.split('\n').filter(Boolean).length} term(s) selected
                  </p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Remarks</label>
                <input
                  type="text"
                  value={formData.remarks}
                  onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                  placeholder="Additional remarks..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-green-400 focus:ring-2 focus:ring-green-100 outline-none text-sm"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t border-gray-200">
              <button
                type="submit"
                className="flex items-center gap-2 px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium"
              >
                <Save size={18} />
                {editingPO ? 'Update PO' : 'Create PO'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="flex items-center gap-2 px-6 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium"
              >
                <X size={18} />
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by PO number, supplier, or contract..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-100 focus:border-green-400"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-100"
          >
            <option value="all">All Status</option>
            {STATUS_OPTIONS.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          {unemailedCount > 0 && (
            <button
              onClick={() => setShowOnlyUnemailed(v => !v)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition ${
                showOnlyUnemailed
                  ? 'bg-amber-500 text-white border-amber-500 shadow-md hover:bg-amber-600'
                  : 'bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100'
              }`}
              title={showOnlyUnemailed
                ? `Showing only ${unemailedCount} unemailed PO${unemailedCount === 1 ? '' : 's'} — click to clear filter`
                : `${unemailedCount} PO${unemailedCount === 1 ? '' : 's'} not yet emailed — click to filter`}
              data-testid="import-po-unemailed-filter-badge"
            >
              <Mail size={14} />
              <span>{unemailedCount} not emailed{showOnlyUnemailed && ' ·'}</span>
              {showOnlyUnemailed && <X size={14} />}
            </button>
          )}
        </div>
      </div>

      {/* PO List */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="p-4 bg-gray-50 border-b border-gray-200">
          <h3 className="font-semibold text-gray-800">Purchase Orders ({filteredPOs.length})</h3>
        </div>
        
        {loading ? (
          <div className="p-8 text-center">
            <RefreshCw size={24} className="animate-spin mx-auto mb-2 text-gray-400" />
            <p className="text-gray-500">Loading purchase orders...</p>
          </div>
        ) : filteredPOs.length === 0 ? (
          <div className="p-8 text-center">
            <ShoppingCart size={48} className="mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500">No purchase orders found</p>
            <button
              onClick={() => { resetForm(); setShowForm(true); }}
              className="mt-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
            >
              Create First PO
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filteredPOs.map(po => (
              <div key={po.id} className="hover:bg-gray-50 transition">
                {/* PO Header Row */}
                <div 
                  className="p-4 flex items-center justify-between cursor-pointer"
                  onClick={() => setExpandedPO(expandedPO === po.id ? null : po.id)}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                      <FileText size={24} className="text-green-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-gray-900">{po.poNumber}</h4>
                        {po.source === 'Sales Contract' && (
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-medium">
                            SC: {po.salesContractNumber || 'Linked'}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">{po.supplierName}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">
                        {po.currency || '₹'} {(po.totalAmount || 0).toLocaleString('en-IN')}
                      </p>
                      <p className="text-xs text-gray-500">{po.poDate}</p>
                    </div>
                    <EmailStatusBadge row={po} testIdSuffix={po.id} />
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(po.status)}`}>
                      {po.status}
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDownloadPDF(po); }}
                        className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition"
                        title="Download PDF"
                        disabled={downloading === po.id}
                      >
                        {downloading === po.id ? <RefreshCw size={16} className="animate-spin" /> : <Download size={16} />}
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); openEmailModal(po); }}
                        className="p-2 bg-teal-100 text-teal-700 rounded-lg hover:bg-teal-200 transition"
                        title="Email PO to Supplier"
                        data-testid={`import-po-email-btn-${po.id}`}
                      >
                        <Mail size={16} />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setHistoryPO(po); }}
                        className="p-2 bg-indigo-100 text-indigo-600 rounded-lg hover:bg-indigo-200 transition"
                        title="Edit History"
                        data-testid={`import-po-history-btn-${po.id}`}
                      >
                        <History size={16} />
                      </button>
                      {canEdit && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleEdit(po); }}
                        className="p-2 bg-amber-100 text-amber-600 rounded-lg hover:bg-amber-200 transition"
                        title="Edit"
                      >
                        <Edit2 size={16} />
                      </button>
                      )}
                      {canDelete && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDelete(po.id); }}
                          className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                    {expandedPO === po.id ? <ChevronUp size={20} className="text-gray-400" /> : <ChevronDown size={20} className="text-gray-400" />}
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedPO === po.id && (
                  <div className="px-4 pb-4 bg-gray-50 border-t border-gray-100">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 pt-4">
                      <div>
                        <p className="text-xs text-gray-500">GST No</p>
                        <p className="font-medium text-sm">{po.supplierGst || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Payment Terms</p>
                        <p className="font-medium text-sm">{po.paymentTerms || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Duty Type</p>
                        <p className="font-medium text-sm">{po.duty || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Expected Delivery</p>
                        <p className="font-medium text-sm">{po.deliveryDate || '-'}</p>
                      </div>
                    </div>
                    
                    {/* Line Items */}
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Scrap Type</th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">HSN</th>
                            <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Qty (MT)</th>
                            <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Rate</th>
                            <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Amount</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {po.items?.map((item, idx) => (
                            <tr key={idx}>
                              <td className="px-3 py-2">{item.scrapType}</td>
                              <td className="px-3 py-2">{item.hsnCode || '-'}</td>
                              <td className="px-3 py-2 text-right">{item.quantity}</td>
                              <td className="px-3 py-2 text-right">₹{parseFloat(item.rate || 0).toLocaleString('en-IN')}</td>
                              <td className="px-3 py-2 text-right font-medium">₹{(item.amount || 0).toLocaleString('en-IN')}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      <EditHistoryModal
        open={!!historyPO}
        onClose={() => setHistoryPO(null)}
        title={historyPO ? `${historyPO.poNumber} — Edit History` : 'Edit History'}
        historyUrl={historyPO ? `/raw-material/purchase-orders/${historyPO.id}/history` : null}
      />
      <EmailModal
        open={!!emailPO}
        onClose={() => setEmailPO(null)}
        title="Email Raw Material PO to Supplier"
        subtitle={emailPO ? `PO ${emailPO.poNumber || emailPO.id} → ${emailPO.supplierName || ''}` : ''}
        emailEndpoint={emailPO ? `/raw-material/purchase-orders/${emailPO.id}/email` : ''}
        defaultTo={emailPO?.supplierEmail || ''}
        snapshotEmail={emailPO?.snapshotEmail || ''}
        defaultSubject={emailPO ? `Raw Material Purchase Order ${emailPO.poNumber || emailPO.id}` : ''}
        defaultBody={emailPO ? [
          `Dear ${emailPO.supplierName || 'Supplier'},`,
          '',
          `Please find attached our Raw Material Purchase Order ${emailPO.poNumber || emailPO.id}.`,
          '',
          `Total Value: ${emailPO.currency || 'USD'} ${(emailPO.totalAmount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`,
          '',
          'Kindly acknowledge receipt and confirm shipment as per the schedule mentioned.',
          '',
          'Regards,',
          'Swift Strips India',
        ].join('\n') : ''}
        onSent={() => fetchPurchaseOrders()}
      />
    </div>
  );
}
